import styled from "@emotion/styled";
import { pendingRewardReducer } from "@stepfinance/farms";
import type { LiquidityPoolPlatforms, LiquidityPools } from "@stepfinance/liquidity";
import type { ConcentratedLiquidityPosition } from "@stepfinance/liquidity";
import { getLiquidityPoolName } from "@stepfinance/liquidity";
import type { StepColumnsType, StepColumnType } from "@stepfinance/react";
import {
  CheckboxFilter,
  formatBalance,
  getColumnSearchProps,
  imageFromPlatformName,
  makeSimpleFilters,
  StepAssetBreakdownPopoverChip,
  StepPopoverCellWrapper,
  StepTable,
  URLIcon,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd/lib/table";
import type { FilterValue } from "antd/lib/table/interface";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { ReactComponent as LPIcon } from "../../../assets/liquidity-pools.svg";
import { DUST_THRESHOLD, useHideDustConfig } from "../../../contexts/app/config";
import { useConcentratedLiquidityContext } from "../../../contexts/concentratedLiquidity";
import { useLanguageContext } from "../../../contexts/language/language";
import type { OwnedPool } from "../../../contexts/liquidity";
import { useLiquidity, useOwnedPools } from "../../../contexts/liquidity";
import { useMarkets } from "../../../contexts/market";
import type { CalculatePriceForDisplayCurrency } from "../../../hooks";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks";
import type { CollapseControlProps } from "../../../hooks/home/useCollapseControl";
import { cache } from "../../../utils/cache";
import { getLiquidityPoolAPY, getTotalLiquidityInUSD } from "../../../utils/pools/utils";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import {
  IconTextContainer,
  NavigationLink,
  PAGE_SIZE_OPTIONS,
  StepPopoverAssetWithIconRow,
  TokenIconLink,
  TokenIconText,
} from "../../shared";
import CellValue from "../../Table/CellValue";
import { TokenIcon } from "../../TokenIcon";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";
import { ConcentratedLiquidity } from "./ConcentratedLiquidity";

// TODO: Lift up
interface GetLiquidityColumnParams {
  title: string;
  dataIndex: string;
  key: string;
  accountPoolMap: Map<string, number>;
  calculatePriceForDisplayCurrency: CalculatePriceForDisplayCurrency;
  displayCurrency: string;
  assetBreakdownString: string;
}

// The commonly-used Pool interfaces match this
interface ConvertableToLiquidityColumn {
  accountAddress: string;
  supply: number | null;
  liquidityInUSD: (number | null)[];
  liquidity: number[];
  mintNames: string[];
  mints: string[];
}

/**
 * Shared by Farms logic and Liquidity Pools logic
 */
export function getLiquidityColumn<T extends ConvertableToLiquidityColumn>(
  params: GetLiquidityColumnParams,
): StepColumnType<T> {
  const { title, accountPoolMap, dataIndex, key, assetBreakdownString } = params;
  return {
    title,
    dataIndex,
    key,
    render(_, record: T) {
      const amount = accountPoolMap.get(record.accountAddress);
      const ratio = (amount || 0) / (record.supply || 0);
      const liquidity = ratio * getTotalLiquidityInUSD(record.liquidityInUSD);
      const LiquidityContent = record.liquidity.map((totalLiquidityAmount, index) => {
        const ownedLiquidityAmount = ratio * totalLiquidityAmount;
        const mintAddress = record.mints[index];
        const liquidityText = formatBalance(ownedLiquidityAmount, cache.getMint(mintAddress)?.decimals);
        const symbol = record.mintNames[index];

        return (
          <StepPopoverAssetWithIconRow
            key={mintAddress}
            tokenIconMintAddress={mintAddress}
            amount={liquidityText}
            symbol={symbol}
          />
        );
      });

      return (
        <StepPopoverCellWrapper>
          <StepAssetBreakdownPopoverChip
            title={assetBreakdownString}
            content={LiquidityContent}
            assetValue={liquidity}
            assetValueDisplay={
              <CellValue cellType="userPositionValue" fontWeight="bold">
                {liquidity}
              </CellValue>
            }
          />
        </StepPopoverCellWrapper>
      );
    },
    className: "column-number",
    sorter: (a, b) => {
      const amountA = accountPoolMap.get(a.accountAddress);
      const amountB = accountPoolMap.get(b.accountAddress);
      const ratioA = (amountA || 0) / (a.supply || 0);
      const liquidityA = ratioA * getTotalLiquidityInUSD(a.liquidityInUSD);
      const ratioB = (amountB || 0) / (b.supply || 0);
      const liquidityB = ratioB * getTotalLiquidityInUSD(b.liquidityInUSD);

      return liquidityA - liquidityB;
    },
  };
}

// TODO: Lift up
/**
 * @param pools An array of pools that the user has participated in
 * @returns A <K, V> map between public key of pool, and number of currently-owned LP tokens
 * including `0` to signify a pool that has been completely withdrawn from
 */
export function useAccountPoolMap(pools: OwnedPool[]): Map<string, number> {
  return useMemo(() => {
    return pools.reduce(function (map, obj) {
      const key = obj.pool.accountAddress.toString();
      let currentAmount = map.has(key) ? map.get(key)! : 0;
      const poolAmount = obj.account.tokenAmount.uiAmount;

      currentAmount += poolAmount;

      map.set(key, currentAmount);
      return map;
    }, new Map<string, number>());
  }, [pools]);
}

export const LPGrid: FC<CollapseControlProps> = ({ activePanels, getPanelKey, onPanelClick }) => {
  const { hideDust } = useHideDustConfig();
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const [filteredInfo, setFilteredInfo] = useState<Record<string, FilterValue | null>>({});
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState("lpGridPaginationSizeSetting", "10");
  const { displayCurrency } = useMarkets();
  const pools: OwnedPool[] = useOwnedPools();
  const { setLiquidityBalance } = useLiquidity();
  const concentratedLiquidityPools = useConcentratedLiquidityContext();

  const {
    currentLanguageData: {
      common,
      components: {
        table: { assetBreakdownPopover },
      },
      pages: {
        dashboard: {
          modules: { liquidity: LPGridText },
        },
      },
    },
  } = useLanguageContext();

  const ownedPools: LiquidityPools[] = useMemo(() => {
    return pools.map((p) => {
      return p.pool;
    });
  }, [pools]);

  const uniquePools: LiquidityPools[] = useMemo(() => {
    const poolSet = new Set();
    return ownedPools.filter(function (item) {
      return poolSet.has(item.mintAddress.toString()) ? false : poolSet.add(item.mintAddress.toString());
    });
  }, [ownedPools]);

  const accountPoolMap: Map<string, number> = useAccountPoolMap(pools);

  // TODO: This is really "allDisplayedPools"
  const allPools: LiquidityPools[] = useMemo(() => {
    return uniquePools.filter((item) => {
      const amount = accountPoolMap.get(item.accountAddress);
      if ((amount || 0) <= 0) {
        return false;
      }

      const ratio = (amount || 0) / (item.supply || 0);
      const liquidity = ratio * getTotalLiquidityInUSD(item.liquidityInUSD);
      if (hideDust) {
        return liquidity > DUST_THRESHOLD;
      }
      return liquidity > 0;
    });
  }, [uniquePools, accountPoolMap, hideDust]);

  const allConcentratedLiquidityPools: ConcentratedLiquidityPosition[] = useMemo(() => {
    return concentratedLiquidityPools.positions.filter((item) => {
      const valueInUSD = item.valueInUSD ?? 0;
      const pendingRewardsInUSD = item.rewards.reduce(pendingRewardReducer, 0);

      const liquidity = valueInUSD + pendingRewardsInUSD;
      if (hideDust) {
        return liquidity > DUST_THRESHOLD;
      }
      return liquidity > 0;
    });
  }, [concentratedLiquidityPools.positions, hideDust]);

  const liquidityColumn: StepColumnType<LiquidityPools> = getLiquidityColumn({
    title: common.value,
    dataIndex: "liquidity",
    key: "liquidity",
    accountPoolMap,
    calculatePriceForDisplayCurrency,
    displayCurrency,
    assetBreakdownString: assetBreakdownPopover.title,
  });

  const totalLPValue = useMemo(() => {
    return allPools.reduce(function (total, pool) {
      const poolTotalLiquidityInUSD = getTotalLiquidityInUSD(pool.liquidityInUSD);
      const amount = accountPoolMap.get(pool.accountAddress);
      const ratio = (amount || 0) / (pool.supply || 0);
      const liquidity = ratio * poolTotalLiquidityInUSD;
      return total + liquidity;
    }, concentratedLiquidityPools.totalValue);
  }, [allPools, accountPoolMap, concentratedLiquidityPools.totalValue]);

  useEffect(() => {
    setLiquidityBalance(totalLPValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalLPValue]);

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(allPools.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSize: parseInt(paginationSetting),
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
    });
  }, [allPools.length, paginationSetting]);

  const columns: StepColumnsType<LiquidityPools> = [
    {
      title: common.platform,
      dataIndex: "kind",
      key: "kind",
      className: "column-string",
      filters: makeSimpleFilters(allPools.map((pool) => pool.kind)),
      filterDropdown: (props) => <CheckboxFilter {...props} />,
      onFilter: (value, record) => record.kind.startsWith(value as LiquidityPoolPlatforms),
      filteredValue: filteredInfo.kind ?? null,
      render(_, { kind }) {
        return (
          <IconTextContainer>
            <URLIcon src={imageFromPlatformName(kind) ?? ""} />
            <TokenIconText bold>
              <CellValue cellType="shortenedText">{kind}</CellValue>
            </TokenIconText>
          </IconTextContainer>
        );
      },
      sorter: (a, b) => a.kind.localeCompare(b.kind),
    },
    {
      title: common.pool,
      dataIndex: "name",
      key: "name",
      className: "column-string",
      render(_, pool) {
        const poolName = getLiquidityPoolName(pool);
        if (pool.kind === "Step Finance") {
          return (
            <IconTextContainer>
              <TokenIcon mintAddress={pool.mints} />
              <TokenIconLink style={{ textAlign: "center" }} href={pool.poolLink}>
                {poolName}
              </TokenIconLink>
            </IconTextContainer>
          );
        } else {
          return (
            <IconTextContainer>
              <TokenIcon mintAddress={pool.mints} />
              <TokenIconText>
                <NavigationLink
                  href={`https://analytics.step.finance/defionsolana/onChainMarkets?mint=${pool.mintAddress}`}
                  target="_blank"
                >
                  {poolName}
                </NavigationLink>
              </TokenIconText>
            </IconTextContainer>
          );
        }
      },
      ...getColumnSearchProps((record) => getLiquidityPoolName(record), "pool"),
      filteredValue: filteredInfo.name ?? null,
      sorter: (a, b) => getLiquidityPoolName(a).localeCompare(getLiquidityPoolName(b)),
    },
    {
      title: "APY",
      dataIndex: "apy",
      key: "apy",
      className: "column-number",
      render(_, record) {
        const apy = getLiquidityPoolAPY(record);
        return <CellValue cellType="percentage">{apy}</CellValue>;
      },
      sorter: (a, b) => getLiquidityPoolAPY(a) - getLiquidityPoolAPY(b),
    },
    { ...liquidityColumn, defaultSortOrder: "descend" },
  ];

  return (
    <>
      {(allPools.length > 0 || concentratedLiquidityPools.positions.length > 0) && (
        <SectionalTable
          activePanels={activePanels}
          panelKey={getPanelKey("LPGrid")}
          onChange={onPanelClick}
          title={common.liquidity}
          value={totalLPValue}
          TitleIcon={LPIcon}
        >
          <>
            {allPools.length > 0 && (
              <ChevronCollapse
                title={LPGridText.standardPools.title}
                value={totalLPValue - concentratedLiquidityPools.totalValue}
                isCollapseChild
              >
                <StepTable
                  rowKey={({ accountAddress }) => accountAddress}
                  columns={columns}
                  dataSource={allPools}
                  pagination={pagination}
                  onChange={(pagination, filters) => {
                    setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
                    setFilteredInfo(filters);
                  }}
                />
              </ChevronCollapse>
            )}
            <ConcentratedLiquidity
              concentratedLiquidityPools={allConcentratedLiquidityPools}
              totalValue={concentratedLiquidityPools.totalValue}
            />
          </>
        </SectionalTable>
      )}
    </>
  );
};

const SectionalTable = styled(ChevronCollapse)`
  margin-top: 20px;
`;
